import React from "react";
import styled from "styled-components";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

interface TimelineItem {
  id: string | number;
  title: string;
  description?: string;
  timestamp: string | Date;
  metadata?: Record<string, any>;
  onClick?: () => void;
}

interface TimelineProps {
  items: TimelineItem[];
  onItemClick?: (item: TimelineItem) => void;
}

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 1rem;
`;

const TimelineItemContainer = styled.div`
  display: flex;
  margin-bottom: 1.5rem;
  position: relative;

  &:last-child {
    .timeline-line {
      display: none;
    }
  }
`;

const TimelineDot = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: white;
  border: 3px solid ${({ theme }) => theme.colors.verae[500]};
  margin-right: 1rem;
  flex-shrink: 0;
  position: relative;
  z-index: 2;
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const TimelineLine = styled.div`
  position: absolute;
  left: 7px;
  top: 16px;
  bottom: -24px;
  width: 2px;
  background-color: ${({ theme }) => theme.colors.verae[200]};
  z-index: 1;
`;

const TimelineContent = styled.div`
  background-color: white;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: 1.25rem;
  flex-grow: 1;
  box-shadow: ${({ theme }) => theme.shadows.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 110px;
  position: relative;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    border-color: ${({ theme }) => theme.colors.verae[200]};
  }
`;

const TimelineTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const TimelineDescription = styled.p`
  margin: 0 0 0.5rem 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.9rem;
`;

const TimelineDate = styled.div`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.verae[500]};
  font-weight: 600;
  /* background-color: ${({ theme }) => theme.colors.verae[50]}; */
  /* padding: 0.3rem 0.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm}; */
  width: fit-content;
  margin-bottom: 1rem;
`;

const TimelineTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1.3rem;
  min-height: 100px;
`;

const TimelineMetadata = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const MetadataItem = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.verae[50]};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 0.4rem 0.6rem;
  font-size: 0.8rem;
  border: 1px solid ${({ theme }) => theme.colors.verae[100]};
`;

const MetadataLabel = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.7rem;
  margin-bottom: 0.2rem;
`;

const MetadataValue = styled.span`
  color: ${({ theme }) => theme.colors.text};
  font-weight: 500;
`;

export const Timeline: React.FC<TimelineProps> = ({ items, onItemClick }) => {
  const navigate = useNavigate();

  const formatDate = (dateString: string | Date) => {
    try {
      const date =
        typeof dateString === "string" ? new Date(dateString) : dateString;
      return format(date, "MMM d, yyyy h:mm a");
    } catch (error) {
      return "Invalid date";
    }
  };

  // Function to render metadata dynamically based on available fields
  const renderDynamicMetadata = (parsedData: any) => {
    if (!parsedData) return null;

    const metadataElements = [];
    let index = 0;

    // Process deviceInfo if available
    if (parsedData.deviceInfo) {
      const deviceInfo = parsedData.deviceInfo;
      // Render common device fields if they exist
      if (deviceInfo.model) {
        metadataElements.push(
          <MetadataItem key={`device-model-${index++}`}>
            <MetadataLabel>Model</MetadataLabel>
            <MetadataValue>{deviceInfo.model}</MetadataValue>
          </MetadataItem>
        );
      }

      if (deviceInfo.serialNumber) {
        metadataElements.push(
          <MetadataItem key={`device-serial-${index++}`}>
            <MetadataLabel>Serial</MetadataLabel>
            <MetadataValue>{deviceInfo.serialNumber}</MetadataValue>
          </MetadataItem>
        );
      }

      if (deviceInfo.make) {
        metadataElements.push(
          <MetadataItem key={`device-make-${index++}`}>
            <MetadataLabel>Make</MetadataLabel>
            <MetadataValue>{deviceInfo.make}</MetadataValue>
          </MetadataItem>
        );
      }
    }

    // Process other important top-level fields
    const topLevelFields = [
      { key: "identifier", label: "ID" },
      { key: "type", label: "Type" },
      { key: "category", label: "Category" },
      { key: "status", label: "Status" },
      { key: "version", label: "Version" },
    ];

    // Add top-level fields that exist in the data
    topLevelFields.forEach((field) => {
      if (parsedData[field.key] && typeof parsedData[field.key] !== "object") {
        metadataElements.push(
          <MetadataItem key={`field-${field.key}-${index++}`}>
            <MetadataLabel>{field.label}</MetadataLabel>
            <MetadataValue>{parsedData[field.key]}</MetadataValue>
          </MetadataItem>
        );
      }
    });

    // Add any custom fields that might be important (up to 3 more)
    const processedKeys = new Set([
      ...topLevelFields.map((f) => f.key),
      "deviceInfo",
      "blockchainData",
      "hash",
      "image",
      "timestamp",
      "date",
    ]);
    let customFieldCount = 0;

    Object.entries(parsedData).forEach(([key, value]) => {
      // Skip already processed keys and objects/arrays
      if (
        processedKeys.has(key) ||
        typeof value === "object" ||
        Array.isArray(value) ||
        customFieldCount >= 3
      ) {
        return;
      }

      // Format the key for display
      const formattedKey = key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
        .trim();

      metadataElements.push(
        <MetadataItem key={`custom-${key}-${index++}`}>
          <MetadataLabel>{formattedKey}</MetadataLabel>
          <MetadataValue>{String(value)}</MetadataValue>
        </MetadataItem>
      );

      customFieldCount++;
    });

    return metadataElements;
  };

  const handleItemClick = (item: TimelineItem) => {
    if (item.onClick) {
      item.onClick();
      return;
    }

    if (onItemClick) {
      onItemClick(item);

      if (item.metadata?.parsed?.blockchainData?.hash) {
        const encodedHash = encodeURIComponent(
          item.metadata.parsed.blockchainData.hash
        );
        navigate(`/record/${encodedHash}`);
      }
    }
  };

  return (
    <TimelineContainer>
      {items.map((item) => (
        <TimelineItemContainer key={item.id}>
          <TimelineDot />
          <TimelineLine className="timeline-line" />
          <TimelineContent onClick={() => handleItemClick(item)}>
            <TimelineDate>{formatDate(item.timestamp)}</TimelineDate>
            <TimelineTitleContainer>
              <TimelineTitle>{item.title}</TimelineTitle>
              {item.description && (
                <TimelineDescription>{item.description}</TimelineDescription>
              )}
            </TimelineTitleContainer>

            <TimelineMetadata>
              {/* Dynamic metadata rendering */}
              {item.metadata?.parsed &&
                renderDynamicMetadata(item.metadata.parsed)}

              {/* Always show blockchain information if available */}
              {item.metadata?.parsed?.blockchainData?.index && (
                <MetadataItem>
                  <MetadataLabel>Block</MetadataLabel>
                  <MetadataValue>
                    #{item.metadata.parsed.blockchainData.index}
                  </MetadataValue>
                </MetadataItem>
              )}

              {/* Hash (shortened) */}
              {item.metadata?.parsed?.blockchainData?.hash && (
                <MetadataItem>
                  <MetadataLabel>Hash</MetadataLabel>
                  <MetadataValue>
                    {item.metadata.parsed.blockchainData.hash.substring(0, 8)}
                    ...
                  </MetadataValue>
                </MetadataItem>
              )}
            </TimelineMetadata>
          </TimelineContent>
        </TimelineItemContainer>
      ))}
    </TimelineContainer>
  );
};
